/**
 * ************************************
 *
 * @module  Recipe.reducer.js
 * @author  Matt P
 * @date    01/19/2021
 * @description redux reducer file for Recipe component
 *
 * ************************************
 */

// ----------------------------------------------------------------------------|
//                                  Imports
// ----------------------------------------------------------------------------|
import ACTIONS from 'store/actions/actionTypes';

// ----------------------------------------------------------------------------|
//                              Initial State
// ----------------------------------------------------------------------------|
const initialState = {
  // Contains images that are to be auto-filled into the image upload component
  autofilledImages: [],
  images: [],
  formData: {},
  // Contains list of fileIds for whom upload failed
  failedUploads: [],
  isDropZonePreviewRequired: false,
  isBtnDisabled: false,
};

// ----------------------------------------------------------------------------|
//                                Utilities
// ----------------------------------------------------------------------------|
// identifier can be the source url of the image in case of auto-filled images
// For images that were not auto-filled, it is the file id
// If an image's source url or its ID matches the identifier param, delete it
// from the array
const getFilteredImages = (identifier, images = []) =>
  images.filter(
    (image) => image.url !== identifier && image.fileId !== identifier
  );

// ----------------------------------------------------------------------------|
//                            Recipe Reducer
// ----------------------------------------------------------------------------|
const Recipe = (state = initialState, action = null) => {
  const { type, data } = action;

  switch (type) {
    case ACTIONS.RECIPE.FETCH_RECIPE_SUCCESS: {
      return {
        ...state,
        fetchedRecipeData: data,
      };
    }

    case ACTIONS.RECIPE.POPULATE_IMAGES: {
      return {
        ...state,
        autofilledImages: data || [],
      };
    }

    case ACTIONS.RECIPE.SAVE_DATA_TO_REDUX: {
      return {
        ...state,
        formData: { ...state.formData, ...data },
      };
    }

    // Clear any errors for image being uploaded
    case ACTIONS.RECIPE.UPLOAD_IMAGE: {
      return {
        ...state,
        isDropZonePreviewRequired: true,
        failedUploads: state.failedUploads.filter(
          (fileId) => fileId !== data.fileId
        ),
      };
    }

    case ACTIONS.RECIPE.UPLOAD_IMAGE_SUCCESS: {
      return {
        ...state,
        autofilledImages: [
          ...state.autofilledImages,
          { ...data, fileId: data.fileId, photo_metadata: data.photo_metadata },
        ],
        isDropZonePreviewRequired: false,
      };
    }

    case ACTIONS.RECIPE.UPLOAD_IMAGE_FAIL: {
      return { ...state, failedUploads: [...state.failedUploads, data.fileId] };
    }

    case ACTIONS.RECIPE.DELETE_IMAGE: {
      return {
        ...state,
        autofilledImages: getFilteredImages(data, state.autofilledImages),
      };
    }

    case ACTIONS.RECIPE.CLEAR_IMAGES: {
      return { ...state, images: [] };
    }

    case ACTIONS.RECIPE.RE_ORDER_IMAGES: {
      return { ...state, autofilledImages: data };
    }

    case ACTIONS.RECIPE.BUTTON_DISBALING: {
      return {
        ...state,
        isBtnDisabled: data.isBtnDisabled,
      };
    }

    default: {
      return state;
    }
  }
};

// ----------------------------------------------------------------------------|
//                          Export - Recipe Reducer
// ----------------------------------------------------------------------------|
export default Recipe;
